import { RfoPhase } from '@/types/workflow';

export const PHASE_ORDER: RfoPhase[] = [
  'PHASE_A', 'PHASE_B', 'PHASE_C', 'PHASE_D',
  'PHASE_E', 'PHASE_F', 'PHASE_G', 'PHASE_H',
];

export const getGateLetter = (phase: RfoPhase): string => phase.replace('PHASE_', '');

export const getPhaseLabel = (phase: RfoPhase): string => {
  const labels: Record<RfoPhase, string> = {
    PHASE_A: 'Discovery & Intake',
    PHASE_B: 'Bid/No-Bid',
    PHASE_C: 'Proposal Planning',
    PHASE_D: 'Compliance & Content',
    PHASE_E: 'Reviews & Quality',
    PHASE_F: 'CEO Review & Sign-off',
    PHASE_G: 'Submission',
    PHASE_H: 'Post-Submission',
  };
  return labels[phase] || phase;
};

export const getPhaseShortLabel = (phase: RfoPhase): string => {
  const labels: Record<RfoPhase, string> = {
    PHASE_A: 'Phase A',
    PHASE_B: 'Phase B',
    PHASE_C: 'Phase C',
    PHASE_D: 'Phase D',
    PHASE_E: 'Phase E',
    PHASE_F: 'Phase F',
    PHASE_G: 'Phase G',
    PHASE_H: 'Phase H',
  };
  return labels[phase] || phase;
};

export const getPhaseGateLabel = (phase: RfoPhase): string => {
  const labels: Record<RfoPhase, string> = {
    PHASE_A: 'Gate A: Intake Complete',
    PHASE_B: 'Gate B: Bid Decision Confirmed',
    PHASE_C: 'Gate C: Kickoff Complete',
    PHASE_D: 'Gate D: Draft Ready for Review',
    PHASE_E: 'Gate E: Final Approved',
    PHASE_F: 'Gate F: CEO Approved',
    PHASE_G: 'Gate G: Submitted',
    PHASE_H: 'Gate H: Post-Submission Complete',
  };
  return labels[phase] || phase;
};

export const getPhaseColor = (phase: RfoPhase): string => {
  const colors: Record<RfoPhase, string> = {
    PHASE_A: '#8B5CF6',
    PHASE_B: '#3B82F6',
    PHASE_C: '#06B6D4',
    PHASE_D: '#10B981',
    PHASE_E: '#F59E0B',
    PHASE_F: '#F59E0B',
    PHASE_G: '#F97316',
    PHASE_H: '#F43F5E',
  };
  return colors[phase] || '#8c8c8c';
};

export const getPhaseDescription = (phase: RfoPhase): string => {
  const descriptions: Record<RfoPhase, string> = {
    PHASE_A: 'Monitor portals, create intake record, notify team, set up workspace',
    PHASE_B: 'Assess eligibility, staffing feasibility, technical fit, record Go/No-Go',
    PHASE_C: 'Kickoff meeting, assign sections, set milestones, start compliance matrix',
    PHASE_D: 'Build compliance matrix, draft all sections, collect attachments',
    PHASE_E: 'Technical review, strategy review, compliance review',
    PHASE_F: 'CEO reviews pipeline summary, approves or rejects for submission',
    PHASE_G: 'Package final submission, verify files, submit, save confirmation',
    PHASE_H: 'Track Q&A, prepare for interviews, conduct retrospective',
  };
  return descriptions[phase] || '';
};

export const getPhaseDeliverables = (phase: RfoPhase): string[] => {
  const deliverables: Record<RfoPhase, string[]> = {
    PHASE_A: ['Opportunity Intake Record', 'RFO Workspace created and shared'],
    PHASE_B: ['Bid/No-Bid Decision Log', 'Win Themes & Strategy Notes'],
    PHASE_C: ['Proposal Schedule', 'Section Ownership List', 'Compliance Matrix skeleton'],
    PHASE_D: ['Compliance Matrix (complete)', 'All sections drafted', 'Attachments collected'],
    PHASE_E: ['Technical Review passed', 'Strategy Review passed', 'Compliance Review passed'],
    PHASE_F: ['CEO Executive Sign-off approved'],
    PHASE_G: ['Final submission package (PDF/ZIP)', 'Submission confirmation evidence'],
    PHASE_H: ['Lessons Learned Notes', 'Updated reusable content'],
  };
  return deliverables[phase] || [];
};

export const getPhaseOwnerRole = (phase: RfoPhase): string => {
  const owners: Record<RfoPhase, string> = {
    PHASE_A: 'Opportunity Finder',
    PHASE_B: 'Enterprise Architect',
    PHASE_C: 'BD Manager',
    PHASE_D: 'Technical Writer',
    PHASE_E: 'Cloud Architect',
    PHASE_F: 'CEO',
    PHASE_G: 'Enterprise Architect / BD Manager',
    PHASE_H: 'BD Manager',
  };
  return owners[phase] || '';
};

/** Returns the Keycloak role keys that own a given phase. */
export const getPhaseOwnerRoles = (phase: RfoPhase): string[] => {
  const owners: Record<RfoPhase, string[]> = {
    PHASE_A: ['OPPORTUNITY_FINDER'],
    PHASE_B: ['ENTERPRISE_ARCHITECT'],
    PHASE_C: ['BD_MANAGER'],
    PHASE_D: ['TECHNICAL_WRITER'],
    PHASE_E: ['CLOUD_ARCHITECT'],
    PHASE_F: ['CEO'],
    PHASE_G: ['ENTERPRISE_ARCHITECT', 'BD_MANAGER'],
    PHASE_H: ['BD_MANAGER'],
  };
  return owners[phase] || [];
};
